// Rótulos, cores e formatações compartilhados pela Fila de Solicitações (suporte) e pelo sino de notificações (solicitante).
import { postUpload } from "utils/WebService";

/**
 * Pedido/devolução/migração com os anexos na MESMA chamada (multipart): o backend grava a ocorrência e os arquivos
 * (aba Documentos da ficha, ligados à ocorrência) juntos. Se um arquivo não puder ser gravado, nada é feito e a
 * mensagem do erro volta para a tela. campos: { observacao, contratosIds: [...], dados: "json" ... }.
 */
export function enviarComAnexos(url, campos, arquivos) {
    const formData = new FormData();
    Object.entries(campos || {}).forEach(([nome, valor]) => {
        if (valor === null || valor === undefined) return;
        if (Array.isArray(valor)) valor.forEach(v => formData.append(nome, v));
        else formData.append(nome, valor);
    });
    (Array.isArray(arquivos) ? arquivos : [arquivos]).filter(Boolean).forEach(arquivo => formData.append("arquivos", arquivo));
    return postUpload(url, formData);
}

export const TIPOS_SOLICITACAO = {
    DEVOLUCAO_FICHA: { label: "Devolução de ficha", icone: "assignment_return", cor: "#AD1457", corEscura: "#F06292" },
    DEVOLUCAO_CONTRATO: { label: "Devolução de contrato", icone: "receipt_long", cor: "#6A1B9A", corEscura: "#BA68C8" },
    MIGRACAO_CARTEIRA: { label: "Migração de carteira", icone: "swap_horiz", cor: "#1565C0", corEscura: "#64B5F6" },
};

export const tipoSolicitacao = (codigo) =>
    TIPOS_SOLICITACAO[codigo] || { label: codigo || "-", icone: "help_outline", cor: "#7b809a", corEscura: "#c3c2b7" };

// Linha do tempo da solicitação (tbSolicitacaoSuporteHistorico.ccAcao)
export const ACOES_HISTORICO = {
    SOLICITADA: { label: "Solicitada", icone: "send", cor: "info" },
    RECUSADA: { label: "Recusada pelo suporte", icone: "block", cor: "error" },
    COBRADA: { label: "Solicitante cobrado", icone: "notifications_active", cor: "warning" },
    REENVIADA: { label: "Reenviada pelo solicitante", icone: "replay", cor: "info" },
    CIENTE: { label: "Solicitante ciente da recusa", icone: "done_all", cor: "secondary" },
    ENCERRADA: { label: "Encerrada pelo suporte", icone: "lock", cor: "secondary" },
    APROVADA: { label: "Aprovada e efetivada", icone: "check_circle", cor: "success" },
    ANEXO_INCLUIDO: { label: "Anexo incluído pelo solicitante", icone: "attach_file", cor: "info" },
    ANEXO_TROCADO: { label: "Anexo trocado pelo solicitante", icone: "swap_horiz", cor: "warning" },
    ANEXO_EXCLUIDO: { label: "Anexo excluído pelo solicitante", icone: "delete_outline", cor: "secondary" },
};

// Avisa as telas abertas (ex.: o aviso no topo da ficha) que uma solicitação foi criada ou mudou
export const EVENTO_SOLICITACOES_ALTERADAS = "ral:solicitacoes-alteradas";
export const avisarSolicitacoesAlteradas = () => window.dispatchEvent(new Event(EVENTO_SOLICITACOES_ALTERADAS));

export const acaoHistorico = (codigo) => ACOES_HISTORICO[codigo] || { label: codigo, icone: "circle", cor: "secondary" };

const HORA = 3600000;
const DIA = 24 * HORA;

/** Tempo decorrido em texto curto: "agora", "35 min", "5h", "2d 4h", "45d". */
export function formatarDuracao(ms) {
    if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "-";
    if (ms < 60000) return "agora";
    if (ms < HORA) return `${Math.floor(ms / 60000)} min`;
    if (ms < DIA) return `${Math.floor(ms / HORA)}h`;
    const dias = Math.floor(ms / DIA);
    const horas = Math.floor((ms % DIA) / HORA);
    return dias >= 10 || horas === 0 ? `${dias}d` : `${dias}d ${horas}h`;
}

// Idade na fila: até 1 dia, até 3 dias, mais de 3 dias (o texto do tempo acompanha a cor)
const CORES_IDADE = {
    recente: { light: "#2E7D32", dark: "#66BB6A" },
    atencao: { light: "#B26A00", dark: "#FFB74D" },
    atrasada: { light: "#C62828", dark: "#EF5350" },
};

export function corIdade(ms, darkMode) {
    const faixa = ms < DIA ? "recente" : ms < 3 * DIA ? "atencao" : "atrasada";
    return CORES_IDADE[faixa][darkMode ? "dark" : "light"];
}

export const listaDe = (res) => res?.list || res?.data || (Array.isArray(res) ? res : []);

/** "#RRGGBB" -> "rgba(r, g, b, alfa)" */
export function comAlfa(hex, alfa) {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(valor.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
}

/** O que a solicitação pede, em uma linha: contrato, carteira de destino ou a ficha inteira. */
export function alvoSolicitacao(s) {
    if (s.tipoSolicitacao === "MIGRACAO_CARTEIRA") {
        // Só parte dos contratos da ficha: lista os títulos pedidos
        return `Para: ${s.nomeCarteiraDestino || "carteira não informada"}${s.contratosMigracao ? ` · Contratos: ${s.contratosMigracao}` : ""}`;
    }
    if (s.tipoSolicitacao === "DEVOLUCAO_CONTRATO") return `Contrato ${s.tituloContrato || "-"}`;
    return "Ficha inteira";
}

/** Busca livre: devedor, CPF/CNPJ (com ou sem pontuação), cliente, contrato, solicitante. */
export function correspondeBusca(s, termo) {
    if (!termo) return true;
    const t = termo.trim().toLowerCase();
    if (!t) return true;
    const soDigitos = t.replace(/\D/g, "");
    const campos = [s.nomeDevedor, s.nomeCliente, s.tituloContrato, s.nomeSolicitante, s.nomeCarteiraDestino, s.cpfCnpjDevedor];
    if (campos.some((c) => c && String(c).toLowerCase().includes(t))) return true;
    return soDigitos.length >= 3 && String(s.cpfCnpjDevedor || "").replace(/\D/g, "").includes(soDigitos);
}
